import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ExportMenu from "@/components/common/ExportMenu";

const TYPE_LABELS = { case_donation: "Case Donation", guardian_monthly: "Guardian Monthly", guardian_one_time: "Guardian One-time" };

export default function PerformanceDonorsList({ range }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState(["all"]);
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/admin/performance/donors", {
      params: { ...range, source: source === "all" ? undefined : source, limit: 100 },
    });
    setItems(data.items);
    setTotal(data.total);
    setSources(["all", ...data.available_sources]);
    setLoading(false);
  }, [range, source]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  return (
    <div data-testid="performance-donors-list">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-amtm-textMuted">Source</span>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-10 w-44" data-testid="performance-source-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sources.map((s) => (
                <SelectItem key={s} value={s} data-testid={`performance-source-option-${s}`}>
                  {s === "all" ? "All sources" : s === "direct" ? "Direct" : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ExportMenu
          url="/admin/performance/export"
          params={{ ...range, source: source === "all" ? undefined : source }}
          filename="amtm_performance_donors"
          testId="performance-donors-export"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
      ) : items.length === 0 ? (
        <p className="text-amtm-textMuted text-center py-12" data-testid="performance-donors-empty">No donors for this range.</p>
      ) : (
        <div className="bg-amtm-surface border border-amtm-border rounded-xl overflow-x-auto" data-testid="performance-donors-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-amtm-textMuted border-b border-amtm-border">
                <th className="p-2">Date</th>
                <th className="p-2">Donor</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Type</th>
                <th className="p-2">Source</th>
                <th className="p-2">Campaign</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amtm-border">
              {items.map((d) => (
                <tr key={d.id} data-testid={`performance-donor-row-${d.id}`}>
                  <td className="p-2">{formatDate(d.created_at)}</td>
                  <td className="p-2">
                    {d.donor_name}
                    <br />
                    <span className="text-xs text-amtm-textMuted">{d.donor_email}</span>
                  </td>
                  <td className="p-2 font-semibold text-amtm-success">₹{Number(d.amount).toLocaleString("en-IN")}</td>
                  <td className="p-2">{TYPE_LABELS[d.type] || d.type}</td>
                  <td className="p-2">{d.attrib_source === "direct" ? "Direct" : d.attrib_source}</td>
                  <td className="p-2">{d.attrib_campaign || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <p className="text-xs text-amtm-textMuted mt-2">{total} donor record(s)</p>}
    </div>
  );
}
